import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { 
  BookOpen, Search, ArrowLeft, Eye, Calendar, User, Tag
} from "lucide-react";
import { API } from "../App";
import axios from "axios";

export default function KnowledgeBasePage() {
  const { articleId } = useParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    if (articleId) {
      fetchArticle(articleId);
    } else {
      fetchArticles();
      fetchCategories();
    }
  }, [articleId, selectedCategory]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      let url = `${API}/knowledge-base`;
      const params = new URLSearchParams();
      if (selectedCategory) params.append("category", selectedCategory);
      if (params.toString()) url += `?${params}`;
      
      const response = await axios.get(url);
      setArticles(response.data);
    } catch (error) {
      console.error("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/knowledge-base/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to load categories");
    }
  };

  const fetchArticle = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/knowledge-base/${id}`);
      setSelectedArticle(response.data);
    } catch (error) {
      console.error("Failed to load article");
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles.filter(article => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      article.title?.toLowerCase().includes(searchLower) ||
      article.content?.toLowerCase().includes(searchLower) ||
      article.tags?.some(tag => tag.toLowerCase().includes(searchLower))
    );
  });

  // Article Detail View
  if (articleId || selectedArticle) {
    const article = selectedArticle;
    
    return (
      <div className="min-h-screen bg-background">
        <header className="bg-primary text-primary-foreground py-8">
          <div className="container mx-auto px-4">
            <Link to="/knowledge-base">
              <Button variant="ghost" className="text-primary-foreground mb-4 hover:bg-white/10">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Knowledge Base
              </Button>
            </Link>
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Young Serif, serif' }}>
              <BookOpen className="h-8 w-8" />
              Knowledge Base
            </h1>
          </div>
        </header>

        <div className="container mx-auto px-4 py-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : article ? (
            <article className="max-w-4xl mx-auto">
              <Card className="card-organic">
                <CardHeader className="border-b">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Badge variant="secondary">{article.category}</Badge>
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {article.views || 0} views
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(article.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Young Serif, serif' }}>
                    {article.title}
                  </CardTitle>
                  {article.tags?.length > 0 && (
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      {article.tags.map((tag, idx) => (
                        <Badge key={idx} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-8">
                  <div 
                    className="prose prose-lg max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ 
                      __html: article.content
                        .replace(/\n\n/g, '</p><p>')
                        .replace(/\n/g, '<br/>')
                        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
                        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
                        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                    }}
                  />
                </CardContent>
              </Card>
              
              {/* Author info */}
              {article.author_name && (
                <div className="flex items-center gap-2 mt-6 text-sm text-muted-foreground justify-center">
                  <User className="h-4 w-4" />
                  <span>Written by {article.author_name}</span>
                </div>
              )}
            </article>
          ) : (
            <Card className="card-organic max-w-4xl mx-auto">
              <CardContent className="p-12 text-center">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">Article not found</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Article List View
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-16">
        <div className="container mx-auto px-4 text-center">
          <BookOpen className="h-16 w-16 mx-auto mb-4 opacity-80" />
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
            Knowledge Base
          </h1>
          <p className="text-lg opacity-90 max-w-2xl mx-auto">
            Helpful articles and guides about TheRentPayer insurance
          </p>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        {/* Search and Filter */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Search articles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 py-6 text-lg"
              data-testid="kb-search"
            />
          </div>
          
          {/* Category Pills */}
          <div className="flex flex-wrap gap-2 justify-center">
            <Badge
              variant={selectedCategory === "" ? "default" : "outline"}
              className="cursor-pointer px-4 py-2"
              onClick={() => setSelectedCategory("")}
            >
              All
            </Badge>
            {categories.map((category) => (
              <Badge
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className="cursor-pointer px-4 py-2"
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Badge>
            ))}
          </div>
        </div>

        {/* Article Grid */}
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredArticles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link 
                  key={article.article_id} 
                  to={`/knowledge-base/${article.article_id}`}
                  onClick={() => setSelectedArticle(null)}
                >
                  <Card 
                    className="card-organic h-full hover:shadow-lg transition-shadow cursor-pointer"
                    data-testid={`article-${article.article_id}`}
                  >
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary">{article.category}</Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {article.views || 0}
                        </span>
                      </div>
                      <CardTitle className="text-lg line-clamp-2" style={{ fontFamily: 'Young Serif, serif' }}>
                        {article.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground text-sm line-clamp-3">
                        {article.content?.replace(/[#*]/g, '').substring(0, 150)}...
                      </p>
                      {article.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {article.tags.slice(0, 3).map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">{tag}</Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <Card className="card-organic">
              <CardContent className="p-12 text-center">
                <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No articles found matching your search</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Help Link */}
        <div className="max-w-4xl mx-auto mt-12 text-center">
          <p className="text-muted-foreground">
            Can't find what you're looking for?{" "}
            <Link to="/support" className="text-primary hover:underline">
              Contact Support
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
